import React, { useEffect, useState } from 'react';
import { formatDate } from 'shared_items/formatConventions';
import { TYPE_COLORS } from './DropdownType';
import { getAreaIcon } from './areaIcons';

const SHEET_TRANSITION_MS = 300;

/**
 * Bottom sheet: lists the most recent records for a single area. Mirrors
 * planner/tab_calendar/viewComponents/DayEventsDialog.jsx shell/animation
 * (mounted/isVisible double-rAF, translate-y slide, rounded-t-2xl panel) —
 * simplified: no day-to-day navigation, no "create" footer action, since an
 * area isn't a date and creating happens from the Records tab.
 */
const AreaRecordsDrawer = ({ open, area, records = [], onClose, onRecordClick }) => {
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const AreaIcon = getAreaIcon(area);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setIsVisible(false);
    }
  }, [open]);

  useEffect(() => {
    if (!mounted || !open) return undefined;
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => setIsVisible(true));
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [mounted, open]);

  useEffect(() => {
    if (!open && mounted) {
      setIsVisible(false);
      const t = window.setTimeout(() => setMounted(false), SHEET_TRANSITION_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);

  if (!mounted) return null;

  const sortedRecords = [...records].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return (
    <div
      className={`fixed inset-0 z-[45] flex items-end justify-center pointer-events-none transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className={`relative z-10 flex max-h-[min(85vh,600px)] w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl pointer-events-auto transform transition-transform duration-300 ease-out ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="shrink-0 border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <AreaIcon className="w-5 h-5 text-gray-600 shrink-0" strokeWidth={2} />
            {area}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">{sortedRecords.length} record{sortedRecords.length === 1 ? '' : 's'}</p>
        </div>

        <div className="min-h-[11rem] flex-1 overflow-y-auto overscroll-contain px-3 py-2">
          {sortedRecords.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-gray-500">No records for this area yet.</p>
          ) : (
            <ul className="flex flex-col gap-1.5 pb-1">
              {sortedRecords.map((record) => (
                <li key={record.id}>
                  <button
                    type="button"
                    onClick={() => onRecordClick?.(record)}
                    className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg p-2.5 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-500">{formatDate(record.date)}</span>
                      <span className={`px-2 py-0.5 text-[10px] text-white rounded-full flex-shrink-0 ${TYPE_COLORS[record.type] || 'bg-gray-400'}`}>
                        {record.type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 truncate">{record.professional}</p>
                    {record.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{record.description}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-gray-50/90 px-3 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-28 rounded-xl px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AreaRecordsDrawer;
